import {
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  HistogramSeries,
  LineSeries,
  LineStyle,
  type Time,
} from "lightweight-charts";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useAppearance } from "../appearanceContext";
import { MA_COLOURS, movingAverage, toWeekly, tradingViewUrl } from "../chartMath";
import { useChartSettings } from "../chartSettingsContext";
import type { ChartData } from "../types";

/**
 * Daily candles for one position, with its fills marked.
 *
 * Average entry and stop are drawn as horizontal lines, so the question the
 * chart exists to answer — where is price relative to my stop — is visible at a
 * glance instead of needing arithmetic.
 */
export function PositionChart({
  tradeId,
  symbol,
  focus,
  title,
  data: provided,
  height = 300,
}: {
  tradeId: number;
  symbol: string;
  /** Which end of the trade to zoom to. Omitted shows the whole range. */
  focus?: "entry" | "exit";
  title?: string;
  /** Pass already-fetched data to render two views from one request. */
  data?: ChartData | null;
  height?: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [fetched, setFetched] = useState<ChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const data = provided ?? fetched;
  const { settings } = useChartSettings();
  const { appearance } = useAppearance();
  const weekly = settings.timeframe === "weekly";

  // Folded once per data/timeframe change rather than on every chart redraw.
  const candles = useMemo(
    () => (data ? (weekly ? toWeekly(data.candles) : data.candles) : []),
    [data, weekly],
  );

  useEffect(() => {
    if (provided !== undefined) return; // parent owns the data
    let cancelled = false;
    api
      // A 50-period average needs 50 bars; six months is 26 weeks, so weekly
      // asks for three years or the longest line would never appear.
      .positionChart(tradeId, weekly ? 36 : undefined)
      .then((d) => !cancelled && setFetched(d))
      .catch((e) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [tradeId, provided, weekly]);

  useEffect(() => {
    if (!container.current || !data || candles.length === 0) return;

    // The charting library wants concrete colours, not CSS variables, so they
    // are read off the document at draw time. The chart is rebuilt whenever
    // the theme changes, which is what makes that safe.
    const token = (name: string) =>
      getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const up = token("--up");
    const down = token("--down");

    const chart = createChart(container.current, {
      height,
      layout: {
        background: { color: "transparent" },
        textColor: token("--muted"),
        fontSize: 11,
      },
      grid: {
        vertLines: { color: token("--chart-grid") },
        horzLines: { color: token("--chart-grid") },
      },
      rightPriceScale: {
        borderColor: token("--chart-axis"),
        scaleMargins: { top: 0.1, bottom: 0.25 },
      },
      timeScale: { borderColor: token("--chart-axis"), rightOffset: 4 },
      crosshair: { mode: 1 },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: up,
      downColor: down,
      borderUpColor: up,
      borderDownColor: down,
      wickUpColor: up,
      wickDownColor: down,
    });
    series.setData(
      candles.map((c) => ({
        time: c.time as Time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );

    // Volume shares the pane but is scaled into the bottom fifth, so it never
    // competes with price for vertical space.
    const volume = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });
    chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volume.setData(
      candles.map((c) => ({
        time: c.time as Time,
        value: c.volume ?? 0,
        color: c.close >= c.open ? `${up}47` : `${down}47`,
      })),
    );

    createSeriesMarkers(series, [
      ...data.entries.map((e) => ({
        time: e.date as Time,
        position: "belowBar" as const,
        color: up,
        shape: "arrowUp" as const,
        text: `B ${e.qty}`,
      })),
      ...data.exits.map((e) => ({
        time: e.date as Time,
        position: "aboveBar" as const,
        color: down,
        shape: "arrowDown" as const,
        text: `S ${e.qty}`,
      })),
    ]);

    if (data.avg_entry) {
      series.createPriceLine({
        price: data.avg_entry,
        color: "#4a6fa5",
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: "entry",
      });
    }
    if (data.avg_exit) {
      series.createPriceLine({
        price: data.avg_exit,
        color: "#8a5fb0",
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: "exit",
      });
    }
    // The extremes the trade reached. Thin and dotted: they are context for
    // the entry and exit lines, not levels of their own.
    for (const [price, title, colour] of [
      [data.highest_price, "MFE", up],
      [data.lowest_price, "MAE", down],
    ] as const) {
      if (!price) continue;
      series.createPriceLine({
        price,
        color: colour,
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        axisLabelVisible: true,
        title,
      });
    }

    if (data.sl_price) {
      series.createPriceLine({
        price: data.sl_price,
        color: down,
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        axisLabelVisible: true,
        title: "stop",
      });
    }

    // Averages sit on the price pane, drawn before the zoom so the visible
    // range is computed once for everything.
    settings.moving_averages.forEach((ma, index) => {
      if (!ma.enabled) return;
      const points = movingAverage(candles, ma.period, ma.kind);
      if (points.length === 0) return;
      const line = chart.addSeries(LineSeries, {
        color: MA_COLOURS[index % MA_COLOURS.length],
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      line.setData(points.map((p) => ({ time: p.time as Time, value: p.value })));
    });

    // Zoom to the relevant end of the trade rather than the whole history, so
    // the entry and exit each get a readable window instead of two identical
    // squashed charts.
    const anchor =
      focus === "entry"
        ? data.entries[0]?.date
        : focus === "exit"
          ? data.exits.at(-1)?.date
          : undefined;

    if (anchor) {
      const times = candles.map((c) => c.time);
      // The bar the date falls in: the last one at or before it. A weekly bar
      // is keyed by its Monday, so looking for an exact match would miss, and
      // looking forward would land on the week after the trade.
      let index = 0;
      for (let i = 0; i < times.length; i++) if (times[i] <= anchor) index = i;
      // Weekly bars cover five times the ground, so the window shrinks to
      // keep roughly the same span of calendar time on screen.
      const before = weekly ? 20 : 45;
      const after = weekly ? 10 : 25;
      const from = times[Math.max(0, index - before)];
      const to = times[Math.min(times.length - 1, index + after)];
      chart.timeScale().setVisibleRange({ from: from as Time, to: to as Time });
    } else {
      chart.timeScale().fitContent();
    }

    const resize = () =>
      container.current && chart.applyOptions({ width: container.current.clientWidth });
    resize();
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      chart.remove();
    };
    // `appearance` is in the dependencies although it is not read here: the
    // colours above are resolved from CSS variables once, so a theme change
    // has to rebuild the chart rather than restyle it.
  }, [data, candles, focus, height, settings, weekly, appearance]);

  const last = candles.at(-1);

  return (
    <div className="chart-card">
      <div className="chart-head">
        <strong>{title ?? symbol}</strong>
        <span className="muted">
          {" "}
          · {weekly ? "1W" : "1D"} · {data?.exchange ?? "NSE"}
        </span>
        {data && (
          /* Opens the symbol in your own TradingView, with your indicators and
             drawings. The chart here stays the one that knows your fills. */
          <a
            className="tv-link"
            href={tradingViewUrl(
              data.exchange,
              data.symbol,
              settings.tradingview_layout,
            )}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${data.exchange}:${data.symbol} in TradingView`}
            onClick={(e) => e.stopPropagation()}
          >
            TradingView ↗
          </a>
        )}
        {last && (
          <span className="right">
            <span className={last.close >= last.open ? "pos" : "neg"}>
              {last.close.toLocaleString("en-IN")}
            </span>
          </span>
        )}
      </div>
      {error && <div className="hint">Couldn't load prices: {error}</div>}
      {!error && !data && <div className="hint">Loading chart…</div>}
      {data && candles.length === 0 && (
        <div className="hint">No price history available for {symbol}.</div>
      )}
      <div ref={container} />
    </div>
  );
}
